/**
 * Messages for the two self-contained embeds (web component, React component).
 *
 * They used to import all 33 locale JSON files statically (~460 KB raw in the entry file, and
 * Rollup flagged every lazy loader in `runtime-locale.ts` as INEFFECTIVE_DYNAMIC_IMPORT). Now only
 * `en` is bundled (it is the guaranteed fallback and makes English instant); every other locale is
 * its own chunk, fetched on demand through the `runtime-locale` loaders, so a visitor downloads the
 * resolved locale only.
 */

import enMessages from "@/messages/en.json";
import { importFromUrl } from "./import-url";
import type { SupportedLocale } from "./locales";
import { IMPORT_RETRY_DELAYS_MS, retryImport } from "./retry-import";
import { loadMessages, type MessagesBundle } from "./runtime-locale";

// Empty `demo` stub — `useTranslations("demo")` is called unconditionally by several components,
// so the namespace must exist to silence next-intl's missing-namespace warning. The real demo
// translations are dynamic-imported by `<DemoMessages>` only in demo mode.
export const EMPTY_DEMO = Object.freeze({});

export interface EmbedMessages {
  /** The locale the messages are actually in (`en` when the requested one failed to load). */
  locale: SupportedLocale;
  messages: MessagesBundle;
}

type Loader = (locale: SupportedLocale) => Promise<MessagesBundle>;

const withDemoStub = (bundle: MessagesBundle): MessagesBundle => ({ ...bundle, demo: EMPTY_DEMO });

/** The bundled English: the first paint of every embed and the fallback of a failed load. */
export const EN_EMBED_MESSAGES: EmbedMessages = {
  locale: "en",
  messages: withDemoStub(enMessages as MessagesBundle),
};

const EN = EN_EMBED_MESSAGES;
const loaded = new Map<SupportedLocale, EmbedMessages>([["en", EN]]);
const inflight = new Map<SupportedLocale, Promise<EmbedMessages>>();

/** Synchronous lookup: `en` always, any other locale once it has loaded; else `null`. */
export function peekEmbedMessages(locale: SupportedLocale): EmbedMessages | null {
  return loaded.get(locale) ?? null;
}

/**
 * Resolve the messages for `locale`. Concurrent calls share one request. A network failure of the
 * chunk is retried quietly (capped, with backoff) before giving up; a load that still fails
 * (offline, chunk 404) resolves to the bundled `en` and is NOT cached, so a later call retries.
 */
export function loadEmbedMessages(
  locale: SupportedLocale,
  load: Loader = loadMessages,
  retryDelays: readonly number[] = IMPORT_RETRY_DELAYS_MS,
): Promise<EmbedMessages> {
  const ready = loaded.get(locale);
  if (ready) return Promise.resolve(ready);
  const pending = inflight.get(locale);
  if (pending) return pending;
  const next = retryImport(
    () => load(locale),
    retryDelays,
    (url, attempt) =>
      importFromUrl<{ default: MessagesBundle }>(url, attempt).then((m) => m.default),
  ).then(
    (bundle) => {
      const result = { locale, messages: withDemoStub(bundle) };
      loaded.set(locale, result);
      inflight.delete(locale);
      return result;
    },
    (error: unknown) => {
      inflight.delete(locale);
      console.warn(`[wexio] the "${locale}" language chunk failed to load; showing English`, error);
      return EN;
    },
  );
  inflight.set(locale, next);
  return next;
}

/** Test seam. */
export function resetEmbedMessagesCache(): void {
  loaded.clear();
  loaded.set("en", EN);
  inflight.clear();
}
